import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Car, MessageCircle, Users, Zap, ArrowRight, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

export const TimelyCTA = () => {
  const benefits = [
    "Book in under 2 minutes",
    "WhatsApp integration",
    "Secure payments",
    "Real-time updates",
    "Premium wash centers",
    "Digital invoices"
  ];

  return (
    <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-white/10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          <Badge variant="secondary" className="bg-white/20 text-white border-white/30 mb-6 sm:mb-8 px-4 py-2 text-sm font-medium inline-flex items-center max-w-full">
            <Zap className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="break-words">Get Started Today</span>
          </Badge>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight break-words">
            Ready to Experience<br />
            <span className="bg-gradient-to-r from-yellow-300 via-orange-300 to-red-300 bg-clip-text text-transparent break-words">Premium Vehicle Wash?</span>
          </h2>

          <p className="text-base sm:text-lg lg:text-xl text-blue-100 mb-8 sm:mb-12 leading-relaxed max-w-4xl mx-auto break-words">
            Join thousands of satisfied customers and wash center owners.
            Book your first wash today or start your wash center business with us and experience the future of vehicle care.
          </p>

          {/* Benefits Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-5 mb-10 sm:mb-12 lg:mb-16 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center justify-center sm:justify-start space-x-2 text-white/90 group min-w-0">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-300 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                <span className="text-xs sm:text-sm md:text-base font-medium break-words text-center sm:text-left">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center mb-10 sm:mb-12 lg:mb-16 max-w-2xl mx-auto">
            <Link to="/booking" className="w-full md:flex-1">
              <Button size="lg" className="w-full h-auto min-h-[56px] bg-white text-blue-600 hover:bg-gray-100 px-6 sm:px-8 py-4 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 group transform hover:scale-105 flex items-center justify-center gap-2">
                <Car className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                <span className="whitespace-nowrap">Book Your Wash</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/onboard-vendor" className="w-full md:flex-1">
              <Button size="lg" className="w-full h-auto min-h-[56px] bg-white text-blue-600 hover:bg-gray-100 px-6 sm:px-8 py-4 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 group transform hover:scale-105 flex items-center justify-center gap-2">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                <span className="whitespace-nowrap">Join as Vendor</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {/* WhatsApp Integration Highlight */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/15 transition-all duration-300 group max-w-4xl mx-auto">
            <CardContent className="p-5 sm:p-6 md:p-8 lg:p-10">
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4 md:space-x-6 mb-4 sm:mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                  <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <div className="text-center sm:text-left min-w-0 flex-1">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2 break-words">WhatsApp Integration</h3>
                  <p className="text-blue-100 text-sm sm:text-base md:text-lg break-words">Get updates directly on WhatsApp</p>
                </div>
              </div>
              <p className="text-center text-blue-100 text-sm sm:text-base md:text-lg leading-relaxed break-words">
                All booking confirmations, updates, and invoices are sent directly to your WhatsApp.
                No need to download additional apps! Experience the future of vehicle care communication.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
